import React from 'react';
import { Swords, Shield, Zap, Backpack } from 'lucide-react';
import type { BattleMoveType } from '../../types/battle';
import type { UserItem } from '../../types/items';

interface BattleControlsProps {
  onMove: (type: BattleMoveType) => void;
  onUseItem: (item: UserItem) => void;
  items: UserItem[];
  disabled?: boolean;
  specialAttacksUsed?: number;
}

export function BattleControls({ 
  onMove, 
  onUseItem,
  items,
  disabled,
  specialAttacksUsed = 0 
}: BattleControlsProps) {
  const MAX_SPECIAL_ATTACKS = 2;
  const remainingSpecials = MAX_SPECIAL_ATTACKS - specialAttacksUsed;

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Basic Moves */}
      <div className="space-y-4">
        <button
          onClick={() => onMove('attack')}
          disabled={disabled}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
        >
          <Swords className="w-5 h-5" />
          Attack
        </button>
        <button
          onClick={() => onMove('defend')}
          disabled={disabled}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
        >
          <Shield className="w-5 h-5" />
          Defend (+15 HP)
        </button>
        <button
          onClick={() => onMove('special')}
          disabled={disabled || remainingSpecials <= 0}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
        >
          <Zap className="w-5 h-5" />
          Special ({remainingSpecials}/{MAX_SPECIAL_ATTACKS})
        </button>
      </div>

      {/* Items */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Backpack className="w-5 h-5 text-yellow-400" />
          <span className="text-sm font-medium text-yellow-400">Items</span>
        </div>
        <div className="space-y-2">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => onUseItem(item)}
              disabled={disabled || item.quantity <= 0}
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm text-white transition-colors"
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-yellow-400" />
                <span>{item.name}</span>
              </div>
              <span className="text-gray-400">x{item.quantity}</span>
            </button>
          ))}
          {items.length === 0 && (
            <p className="text-sm text-gray-400 text-center">No items available</p>
          )}
        </div>
      </div>
    </div>
  );
}